import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Plus,
  GripVertical,
  Trash2,
  MessageSquare,
  CheckSquare,
  Code2,
  FileText,
  Projector,
  Terminal,
  ShieldAlert,
  Network,
  Youtube,
  Search,
  Swords
} from 'lucide-react'
import type { LessonActivity } from '@/types'

interface ActivitySidebarProps {
  activities: LessonActivity[]
  selectedActivityId: string | null
  onSelectActivity: (activityId: string) => void
  onAddActivity: (activityType: string) => void
  onDeleteActivity: (activityId: string) => void
  onReorderActivities: (activities: LessonActivity[]) => void
  loading: boolean
  readOnly?: boolean
}

const SLIDE_TYPES = [
  {
    type: 'pdf_slideshow',
    name: 'PDF Slideshow',
    icon: FileText,
    description: 'Display PDF pages to all participants — captain controls navigation',
    color: 'bg-orange-500/15 text-orange-500'
  },
  {
    type: 'google_slides',
    name: 'Google Slides',
    icon: Projector,
    description: 'Embed a Google Slides presentation — players navigate freely',
    color: 'bg-yellow-500/15 text-yellow-500'
  },
  {
    type: 'youtube_video',
    name: 'YouTube Video',
    icon: Youtube,
    description: 'Embed a YouTube video — captain controls in live mode',
    color: 'bg-red-500/15 text-red-500'
  },
]

const ACTIVITY_TYPES = [
  {
    type: 'mcq',
    name: 'Multiple Choice',
    icon: CheckSquare,
    description: 'Quiz with multiple choice answers',
    color: 'bg-blue-500/15 text-blue-500'
  },
  {
    type: 'coding',
    name: 'Blockly Coding',
    icon: Code2,
    description: 'Programming challenges with block and text coding',
    color: 'bg-cyan-500/15 text-cyan-500'
  },
  {
    type: 'potato_pirates',
    name: 'Potato Pirates',
    icon: Swords,
    description: 'Drag-and-drop card simulator — program ships with attack & control cards to destroy the enemy fleet',
    color: 'bg-orange-500/15 text-orange-500'
  },
  {
    type: 'command_shell',
    name: 'Command Shell',
    icon: Terminal,
    description: 'Interactive terminal — students investigate with real network commands',
    color: 'bg-emerald-500/15 text-emerald-500'
  },
  {
    type: 'phishing_quiz',
    name: 'Phishing Quiz',
    icon: ShieldAlert,
    description: '52-question bank across 8 categories — filter & pick which phishing emails to include',
    color: 'bg-red-500/15 text-red-500'
  },
  {
    type: 'packet_analysis',
    name: 'Packet Analysis',
    icon: Network,
    description: '30-question bank — students analyze network captures to identify attacks',
    color: 'bg-violet-500/15 text-violet-500'
  },
  {
    type: 'cyber_hygiene_quiz',
    name: 'Cyber Hygiene Quiz',
    icon: ShieldAlert,
    description: '50-question bank across 10 categories — annual cyber awareness training',
    color: 'bg-amber-500/15 text-amber-500'
  },
  {
    type: 'coding_quiz',
    name: 'Coding Quiz',
    icon: Code2,
    description: '100-question Python coding bank across 10 categories — variables, loops, functions & more',
    color: 'bg-indigo-500/15 text-indigo-500'
  },
  {
    type: 'python_repl',
    name: 'Python Coding',
    icon: Code2,
    description: 'Write & run Python code with test cases — 100-question bank across 8 categories',
    color: 'bg-teal-500/15 text-teal-500'
  },
  {
    type: 'breach_check',
    name: 'Breach Check',
    icon: Search,
    description: 'Students check if an email has been in known data breaches (HIBP)',
    color: 'bg-rose-500/15 text-rose-500'
  },
  {
    type: 'password_check',
    name: 'Password Check',
    icon: ShieldAlert,
    description: 'Students check password strength against known breaches (k-anonymity)',
    color: 'bg-amber-500/15 text-amber-500'
  }
]

const ALL_TYPES = [...SLIDE_TYPES, ...ACTIVITY_TYPES]

export function ActivitySidebar({
  activities,
  selectedActivityId,
  onSelectActivity,
  onAddActivity,
  onDeleteActivity,
  onReorderActivities,
  loading,
  readOnly = false
}: ActivitySidebarProps) {
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [draggedItem, setDraggedItem] = useState<string | null>(null)

  const getActivityIcon = (activityType: string) => {
    const activityConfig = ALL_TYPES.find(t => t.type === activityType)
    const IconComponent = activityConfig?.icon || MessageSquare
    return IconComponent
  }

  const getActivityColor = (activityType: string) => {
    const activityConfig = ALL_TYPES.find(t => t.type === activityType)
    return activityConfig?.color || 'bg-[var(--color-surface-deep)] text-[var(--color-text-muted)]'
  }

  const isSlide = (activityType: string) => activityType === 'pdf_slideshow' || activityType === 'google_slides' || activityType === 'youtube_video'

  const handleDragStart = (e: React.DragEvent | MouseEvent | TouchEvent | PointerEvent, activityId: string) => {
    setDraggedItem(activityId)
    if ('dataTransfer' in e) {
      e.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, targetActivityId: string) => {
    e.preventDefault()
    
    if (!draggedItem || draggedItem === targetActivityId) {
      setDraggedItem(null)
      return
    }

    const draggedIndex = activities.findIndex(a => a.id === draggedItem)
    const targetIndex = activities.findIndex(a => a.id === targetActivityId)
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null)
      return
    }

    const reorderedActivities = [...activities]
    const [draggedActivity] = reorderedActivities.splice(draggedIndex, 1)
    reorderedActivities.splice(targetIndex, 0, draggedActivity)

    onReorderActivities(reorderedActivities)
    setDraggedItem(null)
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }

  const renderActivityItem = (activity: LessonActivity, index: number) => {
    const IconComponent = getActivityIcon(activity.activity_type)
    const isSelected = activity.id === selectedActivityId
    const slideLabel = isSlide(activity.activity_type) ? 'Slide' : activity.activity_type === 'coding' ? 'Blockly Coding' : activity.activity_type === 'python_repl' ? 'Python Coding' : activity.activity_type === 'mcq' ? 'Multiple Choice' : activity.activity_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

    return (
      <motion.div
        key={activity.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        draggable={!readOnly}
        onDragStart={readOnly ? undefined : (e) => handleDragStart(e, activity.id)}
        onDragOver={readOnly ? undefined : handleDragOver}
        onDrop={readOnly ? undefined : (e) => handleDrop(e, activity.id)}
        className={`group p-3 rounded-lg border cursor-pointer transition-all ${
          isSelected
            ? 'bg-[var(--color-primary-subtle)] border-[var(--color-primary)]'
            : 'bg-[var(--color-surface)] border-[var(--color-border)] hover:border-[var(--color-secondary)] hover:shadow-[0_0_12px_rgba(0,204,255,0.08)]'
        } ${draggedItem === activity.id ? 'opacity-50' : ''}`}
        onClick={() => onSelectActivity(activity.id)}
      >
        <div className="flex items-start space-x-3">
          {!readOnly && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
              <GripVertical className="w-4 h-4 text-[var(--color-muted)]" />
            </div>
          )}
          <div className={`p-2 rounded-lg ${getActivityColor(activity.activity_type)}`}>
            <IconComponent className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-[var(--color-text)] truncate">
                {activity.activity_data?.question || activity.activity_data?.title || activity.title || `Item ${index + 1}`}
              </h3>
              <div className="flex items-center space-x-1">
                {activity.time_limit_seconds && (
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatDuration(activity.time_limit_seconds)}
                  </span>
                )}
                {!readOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteActivity(activity.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-[var(--color-muted)] hover:text-[var(--color-danger)] transition-all"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <div className="text-sm text-[var(--color-text-muted)] mt-1">{slideLabel}</div>
            {activity.instruction && (
              <div className="text-xs text-[var(--color-text-muted)] mt-1 truncate">{activity.instruction}</div>
            )}
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between">
          <h2 className="ds-section-title">Lesson Content</h2>
          {!readOnly && (
          <div className="relative">
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              disabled={loading}
              className="p-2 text-[var(--color-secondary)] bg-[var(--color-secondary-glow)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>

            {/* Add Menu — split into Slides and Activities */}
            {showAddMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-12 w-72 bg-[var(--color-dropdown-bg)] rounded-lg shadow-lg border border-[var(--color-border)] py-2 z-10"
              >
                {/* Slides section */}
                <div className="ds-section-title px-3 py-1.5 bg-[var(--color-surface-deep)] border-b border-[var(--color-border)]">
                  ▸ Slides
                </div>
                {SLIDE_TYPES.map((type) => {
                  const IconComponent = type.icon
                  return (
                    <button
                      key={type.type}
                      onClick={() => { onAddActivity(type.type); setShowAddMenu(false) }}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--color-surface-hover)] flex items-center space-x-3"
                    >
                      <div className={`p-2 rounded-lg ${type.color}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[var(--color-text)]">{type.name}</div>
                        <div className="text-sm text-[var(--color-text-muted)] truncate">{type.description}</div>
                      </div>
                    </button>
                  )
                })}

                {/* Activities section */}
                <div className="ds-section-title px-3 py-1.5 bg-[var(--color-surface-deep)] border-y border-[var(--color-border)] mt-1">
                  ▸ Activities
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {ACTIVITY_TYPES.map((type) => {
                    const IconComponent = type.icon
                    return (
                      <button
                        key={type.type}
                        onClick={() => { onAddActivity(type.type); setShowAddMenu(false) }}
                        className="w-full text-left px-3 py-2 hover:bg-[var(--color-surface-hover)] flex items-center space-x-3"
                      >
                        <div className={`p-2 rounded-lg ${type.color}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--color-text)]">{type.name}</div>
                          <div className="text-sm text-[var(--color-text-muted)] truncate">{type.description}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </motion.div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Content list — all items in sequence order */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-[var(--color-muted)] mb-3">
              <Plus className="w-8 h-8 mx-auto" />
            </div>
            <p className="text-[var(--color-text-muted)] mb-2">No content yet</p>
            <p className="text-sm text-[var(--color-text-muted)]">Add slides or activities to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activities.map((activity, index) => renderActivityItem(activity, index))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-surface-deep)]">
        <div className="text-sm text-[var(--color-text-muted)]">
          {activities.length} {activities.length === 1 ? 'item' : 'items'}
        </div>
      </div>

      {/* Click outside to close menu */}
      {!readOnly && showAddMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowAddMenu(false)}
        />
      )}
    </div>
  )
}